/**
 * client/render/renderer.ts — BO2 world renderer (GameRenderer). OWNER: RENDER agent.
 *
 * Passes per frame:
 *   1. sun shadow maps (static map once after load, players every frame)          shadows.ts
 *   2. world + entities + sky into an MSAA RGBA8 target (sqrt-encoded colour)       world.ts, players.ts, missiles.ts, fx.ts, sky.ts
 *   3. post: mp_hijacked colour LUT -> canvas                                       post.ts
 *   4. viewmodel scene straight to the canvas (clear depth; three built-in output encoding)
 * The BO2 lighting/encoding model is documented in glsl.ts; all ESTIMATEs are listed in
 * docs/RENDER.md-style comments at the top of each module.
 *
 * Coordinates: map.glb is already three space (inches). Everything from ClientWorld/RefDef is CoD
 * space -> codToThree / codAnglesToThreeEuler (shared/math.ts).
 *
 * Debug hook (tests, console): window.__render — see RenderDebugHandle below.
 */
import * as THREE from 'three';
import { R_ZNEAR } from '@shared/constants';
import { codAnglesToThreeEuler, codToThree, type Vec3 } from '@shared/math';
import type { MapName } from '@shared/protocol/control';
import type { ClientWorld, GameRenderer, RefDef, RenderInfo, Viewmodel } from '../types';
import { MAX_PRIMARY_LIGHTS, createBo2Uniforms, type Bo2Uniforms } from './glsl';
import { DEFAULT_LIGHTMAP_SCALE, anglesToForward, exposureScale, type FogParams } from './lighting';
import { PostPass } from './post';
import { SunShadows } from './shadows';
import { createSkyMesh, loadSkyCube } from './sky';
import { loadWorld, type BuiltWorld } from './world';
import { EntityRenderer } from './entities';
import { effectivePixelRatio, maxPixelsFromQuery } from './resolution';

interface MapMeta {
  bounds: { playerCollision: { min: number[]; max: number[] } };
  lighting: {
    lightmaps?: ({ baked: string; packed?: string } | null)[];
    lightmapPacked?: string;
    lightmapScale?: number;
    sun: { dirTo: number[]; color: number[]; exposure?: number; angles?: number[] };
    primaryLights?: { type: number; color: number[]; dir: number[]; origin: number[]; radius: number; cosHalfFovOuter: number; cosHalfFovInner: number }[];
    sunPrimaryLightIndex?: number;
  };
  fog?: FogParams;
  sky: { faces?: string[]; skyBoxRotationSize?: number[]; equirect: string };
  mapCenter?: number[];
}

export interface DebugView { name: string; pos: number[]; target?: number[]; fov?: number; ortho?: number }

export interface RenderDebugHandle {
  /** three-space camera override (null = follow the game refdef). */
  setDebugCamera(v: DebugView | null): void;
  /** The 5 + 2 viewpoints tools/map/preview/render.mjs uses (top, intermission, spawn0/7/14/21/28, overview). */
  views(): DebugView[];
  setExposure(k: number): void;
  getExposure(): number;
  setOption(name: string, value: number | boolean | string): void;
  stats(): Record<string, unknown>;
  /** Spawn a test fx at a three-space position: 'explosion' | 'blood' | 'splash' | 'impact'. */
  fx(kind: string, threePos: number[], surface?: number): void;
  inspect(): Record<string, unknown>[];
  /** Debug camera looking at a player from `dist` inches in front (yaw offset deg), at chest height. */
  focusPlayer(clientNum: number, dist?: number, yawOffset?: number, height?: number): boolean;
  /** Debug camera on world.missiles[index]; returns its state. */
  focusMissile(index?: number, dist?: number): Record<string, unknown> | null;
  ready: boolean;
}

declare global {
  interface Window { __render?: RenderDebugHandle }
}

/** Longest the first viewmodel draw waits for its parallel shader compile (ESTIMATE). */
const VM_WARM_MAX_MS = 1000;

/**
 * renderer.compileAsync over EVERY object under `root`, hidden ones included (three's compile only visits
 * visible objects): visibility is forced on just for the synchronous material collection, then restored.
 */
export function compileAllAsync(r: THREE.WebGLRenderer, root: THREE.Object3D, camera: THREE.Camera, target: THREE.Scene | null = null): Promise<unknown> {
  const hidden: THREE.Object3D[] = [];
  root.traverse((o) => { if (!o.visible) { hidden.push(o); o.visible = true; } });
  let p: Promise<unknown>;
  try {
    p = typeof r.compileAsync === 'function' ? r.compileAsync(root, camera, target) : Promise.resolve(r.compile(root, camera, target));
  } finally {
    for (const o of hidden) o.visible = false;
  }
  return p;
}

/** Every texture referenced by the materials (uniforms or standard maps) under `root`. */
export function collectTextures(root: THREE.Object3D): THREE.Texture[] {
  const out = new Set<THREE.Texture>();
  const add = (v: unknown) => { if ((v as THREE.Texture)?.isTexture) out.add(v as THREE.Texture); };
  root.traverse((o) => {
    const m = (o as THREE.Mesh).material;
    if (!m) return;
    for (const mat of Array.isArray(m) ? m : [m]) {
      for (const v of Object.values(mat as unknown as Record<string, unknown>)) add(v);
      const u = (mat as THREE.ShaderMaterial).uniforms;
      if (u) for (const k in u) add(u[k].value);
    }
  });
  return [...out];
}

export class Bo2Renderer implements GameRenderer {
  private renderer!: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  // far plane: the ocean (water_ocean_mp_hijacked_distant) spans +-4.3 M in (gfxWorld bounds) and must reach the
  // horizon; with near 4 the depth precision at deck distances is unaffected by the far value.
  private readonly camera = new THREE.PerspectiveCamera(51.08, 16 / 9, R_ZNEAR, 6e6);
  private readonly debugCam = new THREE.PerspectiveCamera(65, 16 / 9, 4, 6e6);
  private readonly orthoCam = new THREE.OrthographicCamera(-1, 1, 1, -1, 1, 40000);
  private debugView: DebugView | null = null;
  private lastWorld: ClientWorld | null = null;
  private viewmodel: Viewmodel | null = null;
  private readonly U: Bo2Uniforms = createBo2Uniforms();
  private post!: PostPass;
  private shadows!: SunShadows;
  private world: BuiltWorld | null = null;
  private entities: EntityRenderer | null = null;
  private meta: MapMeta | null = null;
  private spawnsJson: { spawns?: Record<string, { origin: number[]; angles: number[] }[]>; intermission?: { origin: number[]; angles: number[] }[]; mapCenter?: number[] } | null = null;
  private exposureK = 0.2;
  private readonly tmp: Vec3 = [0, 0, 0];
  private readonly euler: Vec3 = [0, 0, 0];
  private width = 1280;
  private height = 720;
  private timeMs = 0;
  readonly info: RenderInfo = { drawCalls: 0, triangles: 0, textures: 0 };
  private readonly extraInfo = { shadowCalls: 0, mainCalls: 0, vmCalls: 0, frameMs: 0 };
  /** App-requested pixel ratio and the one actually used (pixel budget, resolution.ts). */
  private reqPixelRatio = 1;
  private pixelRatio = 1;
  private maxPixels = 0;
  // ---- GPU context loss (macOS GPU switch, driver reset, memory pressure, Safari backgrounding)
  private mapName: MapName | null = null;
  private gpu: 'ok' | 'lost' | 'rebuilding' | 'failed' = 'ok';
  private gpuOverlay: HTMLDivElement | null = null;
  private gpuLostTimer = 0;
  private gpuRestores = 0;
  /** Viewmodel programs are compiled (in parallel) before its first draw: 'none' -> 'compiling' -> 'ready'. */
  private vmWarm: 'none' | 'compiling' | 'uploading' | 'ready' = 'none';
  private vmTexQueue: THREE.Texture[] = [];
  private vmWarmStart = 0;

  init(canvas: HTMLCanvasElement): void {
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true, powerPreference: 'high-performance', stencil: false });
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.NoToneMapping;
    this.renderer.autoClear = false;
    this.renderer.info.autoReset = false;
    this.renderer.setClearColor(0x000000, 1);
    this.post = new PostPass(4);
    this.shadows = new SunShadows(this.U);
    this.scene.matrixWorldAutoUpdate = true;
    this.maxPixels = maxPixelsFromQuery(location.search);
    this.installDebugHandle();
    // WebGL context loss. three.js itself preventDefault()s the loss (so the browser may restore) and
    // re-initialises its own state on restore, but our static GPU data cannot be re-uploaded from the CPU:
    // world geometry frees its arrays after upload and texture arrays are filled by hand from ImageBitmaps
    // that are closed afterwards (both to save ~hundreds of MB). So on restore the whole map/entity set is
    // rebuilt from the (HTTP-cached) assets; until then nothing is drawn and an overlay explains why.
    canvas.addEventListener('webglcontextlost', (e) => {
      e.preventDefault();
      this.gpu = 'lost';
      if (window.__render) window.__render.ready = false;
      this.showGpuOverlay('Graphics device reset. Restoring\u2026', false);
      clearTimeout(this.gpuLostTimer);
      // no restore within 5 s (ESTIMATE; Safari may never restore): offer a reload
      this.gpuLostTimer = window.setTimeout(() => { if (this.gpu === 'lost') this.showGpuOverlay('Graphics device was reset and did not come back.', true); }, 5000);
    });
    canvas.addEventListener('webglcontextrestored', () => {
      clearTimeout(this.gpuLostTimer);
      void this.rebuildAfterRestore();
    });
  }

  private async rebuildAfterRestore(): Promise<void> {
    if (!this.mapName) { this.gpu = 'ok'; this.hideGpuOverlay(); return; }
    this.gpu = 'rebuilding';
    this.showGpuOverlay('Restoring graphics\u2026', false);
    try {
      // old GPU objects died with the context: drop them without dispose() (that would delete foreign GL names)
      this.entities = null;
      this.world = null;
      this.scene.clear();
      this.shadows.clearStaticCasters();
      this.shadows.dynamicScene.clear();
      this.loadCount.builds++;
      this.loadP = this.buildMap(this.mapName);
      await this.loadP;
      this.gpu = 'ok';
      this.gpuRestores++;
      this.hideGpuOverlay();
    } catch (err) {
      console.error('[render] rebuild after context restore failed', err);
      this.gpu = 'failed';
      this.showGpuOverlay('Graphics could not be restored after a device reset.', true);
    }
  }

  private showGpuOverlay(text: string, reload: boolean): void {
    if (typeof document === 'undefined') return;
    let o = this.gpuOverlay;
    if (!o) {
      o = document.createElement('div');
      o.id = 'gpu-reset-overlay';
      o.style.cssText = 'position:fixed;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:16px;'
        + 'background:rgba(0,0,0,0.82);color:#fff;font:600 20px/1.4 system-ui,sans-serif;z-index:2147483000;text-align:center;padding:16px';
      document.body.appendChild(o);
      this.gpuOverlay = o;
    }
    o.textContent = '';
    const t = document.createElement('div');
    t.textContent = text;
    o.appendChild(t);
    if (reload) {
      const b = document.createElement('button');
      b.textContent = 'Reload';
      b.style.cssText = 'font:600 18px system-ui,sans-serif;padding:10px 28px;border-radius:6px;border:0;background:#f7a51c;color:#000;cursor:pointer';
      b.onclick = () => location.reload();
      o.appendChild(b);
    }
    o.style.display = 'flex';
  }

  private hideGpuOverlay(): void {
    if (this.gpuOverlay) this.gpuOverlay.style.display = 'none';
  }

  /**
   * Load a map once. A second call for the same map while it loads (the app can receive `load` twice) or after
   * it loaded returns the same promise instead of building a second copy of the world into the scene.
   */
  loadMap(map: MapName, onProgress?: (frac: number, label: string) => void): Promise<void> {
    if (this.loadP && this.loadPMap === map) {
      this.loadCount.deduped++;
      return this.loadP;
    }
    this.loadPMap = map;
    this.loadCount.builds++;
    this.loadP = this.buildMap(map, onProgress);
    this.loadP.catch(() => { if (this.loadPMap === map) { this.loadP = null; this.loadPMap = null; } });
    return this.loadP;
  }
  private loadP: Promise<void> | null = null;
  private loadPMap: MapName | null = null;
  private readonly loadCount = { builds: 0, deduped: 0, startMs: 0, warmMs: 0, endMs: 0 };

  private async buildMap(map: MapName, onProgress?: (frac: number, label: string) => void): Promise<void> {
    this.mapName = map;
    this.loadCount.startMs = Math.round(performance.now());
    const base = `/assets/maps/${map}/`;
    onProgress?.(0, 'meta');
    const [meta, spawns] = await Promise.all([
      fetch(base + 'meta.json').then((r) => r.json() as Promise<MapMeta>),
      fetch(base + 'spawns.json').then((r) => (r.ok ? r.json() : null)).catch(() => null),
    ]);
    this.meta = meta;
    this.spawnsJson = spawns;
    const L = meta.lighting;
    const U = this.U;
    // ---- sun / exposure / fog uniforms [MAP Result table, META lighting/fog]
    const sd = L.sun.dirTo;
    U.uSunDir.value.set(sd[0], sd[1], sd[2]).normalize();
    U.uSunColor.value.set(L.sun.color[0], L.sun.color[1], L.sun.color[2]);
    this.exposureK = exposureScale(L.sun.exposure ?? 3.25);
    U.uExposure.value = this.exposureK;
    U.uLmScale.value = L.lightmapScale ?? DEFAULT_LIGHTMAP_SCALE;
    const fog = meta.fog;
    if (fog) {
      U.uFogColor.value.set(fog.fogColor[0], fog.fogColor[1], fog.fogColor[2]);
      U.uSunFogColor.value.set(fog.sunFogColor[0], fog.sunFogColor[1], fog.sunFogColor[2]);
      const f = anglesToForward([0, 0, 0], fog.sunFogPitch, fog.sunFogYaw);
      codToThree(this.tmp, f as Vec3);
      U.uSunFogDir.value.set(this.tmp[0], this.tmp[1], this.tmp[2]).normalize();
      U.uFogA.value.set(fog.baseDist, 1 / fog.halfDist, fog.baseHeight, 1 / fog.halfHeight);
      U.uFogB.value.set(fog.fogOpacity, fog.sunFogOpacity, fog.sunFogInner, fog.sunFogOuter);
    } else U.uFogB.value.set(0, 0, 0, 90);
    // primary spot/omni lights (comworld; index 0 none, 1 = sun) -> shared uniforms [MAP Open items]
    const pls = L.primaryLights ?? [];
    for (let i = 0; i < Math.min(pls.length, MAX_PRIMARY_LIGHTS); i++) {
      const l = pls[i];
      if (i < 2 || l.radius <= 0) { U.uPLPos.value[i].set(0, 0, 0, 0); continue; }
      codToThree(this.tmp, l.origin as Vec3);
      U.uPLPos.value[i].set(this.tmp[0], this.tmp[1], this.tmp[2], l.radius);
      // GfxLight.dir points from the lit surface toward the light (like the sun's dirTo): spot axis = -dir
      codToThree(this.tmp, [-l.dir[0], -l.dir[1], -l.dir[2]]);
      const spot = l.type === 2 && l.cosHalfFovOuter > 0;
      U.uPLDir.value[i].set(this.tmp[0], this.tmp[1], this.tmp[2], spot ? l.cosHalfFovOuter : -1);
      U.uPLColor.value[i].set(l.color[0], l.color[1], l.color[2], spot ? Math.max(l.cosHalfFovInner, l.cosHalfFovOuter + 1e-3) : 1);
    }
    if (meta.sky.skyBoxRotationSize) U.uSkyRot.value.set(meta.sky.skyBoxRotationSize[0], meta.sky.skyBoxRotationSize[1], meta.sky.skyBoxRotationSize[2]);

    // ---- lightmap (packed 3-page) + sky cube
    onProgress?.(0.02, 'lightmap');
    const packedName = L.lightmaps?.[0]?.packed ?? L.lightmapPacked;
    const tl = new THREE.TextureLoader();
    const jobs: Promise<unknown>[] = [];
    if (packedName) {
      jobs.push(tl.loadAsync(base + packedName).then((t) => {
        t.flipY = false; t.colorSpace = THREE.NoColorSpace; t.generateMipmaps = false;
        t.minFilter = THREE.LinearFilter; t.magFilter = THREE.LinearFilter;
        t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping;
        t.premultiplyAlpha = false;
        U.uLmPacked.value = t;
      }));
    }
    if (meta.sky.faces?.length === 6) jobs.push(loadSkyCube(base, meta.sky.faces).then((c) => { U.uSkyCube.value = c; }));
    await Promise.all(jobs);
    if (U.uLmPacked.value) this.renderer.initTexture(U.uLmPacked.value);

    // ---- world
    const q = new URLSearchParams(location.search);
    this.world = await loadWorld(base + 'map.glb', this.renderer, U, {
      // world normal maps cost ~90 MB of VRAM: skip them on low-memory devices unless forced (?normalmaps=1)
      normalMaps: q.get('normalmaps') === '1' || (q.get('normalmaps') !== '0' && ((navigator as { deviceMemory?: number }).deviceMemory ?? 8) >= 8),
      anisotropy: Math.min(8, this.renderer.capabilities.getMaxAnisotropy()),
      sunPrimaryLightIndex: L.sunPrimaryLightIndex ?? 1,
      lightmapScale: U.uLmScale.value,
      onProgress: (f, l) => onProgress?.(0.04 + f * 0.7, l),
    });
    this.scene.add(this.world.root);
    this.scene.add(createSkyMesh(U));

    // ---- sun shadows over the playable yacht [meta.bounds.playerCollision, three space]
    const pc = meta.bounds.playerCollision;
    const box = new THREE.Box3(new THREE.Vector3(pc.min[0] - 400, pc.min[1] - 150, pc.min[2] - 400), new THREE.Vector3(pc.max[0] + 400, pc.max[1] + 900, pc.max[2] + 400));
    this.shadows.setup(box, U.uSunDir.value, 1.5, this.renderer.capabilities.maxTextureSize >= 8192 ? 8192 : 4096);
    this.shadows.addStaticCasters(this.world.casters);

    // ---- entities (players, missiles, fx)
    onProgress?.(0.76, 'players');
    this.entities = new EntityRenderer(this.renderer, U, this.scene, this.shadows, this.world.probes);
    await this.entities.load((f, l) => onProgress?.(0.76 + f * 0.23, l));
    // compile + draw once offscreen with every entity model in view (avoids first-frame hitches)
    onProgress?.(0.99, 'shaders');
    this.loadCount.warmMs = Math.round(performance.now());
    await this.warmUp();
    this.loadCount.endMs = Math.round(performance.now());
    if (window.__render) window.__render.ready = true;
    onProgress?.(1, 'done');
  }

  /**
   * Load-time warm-up. renderer.compile() alone left 0.5 s frames at MATCH/SPAWN (verify perf-spikes.mjs,
   * 533 + 500 ms in Chromium, 181 + 430 ms in WebKit): it only covered the empty map (no player/projectile
   * programs yet), and the GPU-side work (pipeline creation, uploads, mip generation) is deferred to the first
   * real draw. So: put hidden-afterwards instances of every player body/weapon/projectile model in front of a
   * spawn view, compile (in parallel where KHR_parallel_shader_compile exists), draw the full frame (shadows,
   * world, post) offscreen from two viewpoints, and block on a 1-pixel readback, all behind the loading screen.
   */
  private async warmUp(): Promise<void> {
    const r = this.renderer;
    const views = this.debugViews();
    const picks = ['spawn0', 'overview'].map((n) => views.find((v) => v.name === n)).filter((v): v is DebugView => !!v);
    const cam0 = picks.length ? (this.applyDebugView(picks[0]) as THREE.PerspectiveCamera) : this.camera;
    cam0.updateMatrixWorld();
    const dir = cam0.getWorldDirection(new THREE.Vector3());
    const at = cam0.position.clone().addScaledVector(dir, 150);
    at.y -= 50;
    const show = this.entities?.prewarm(at) ?? (() => {});
    show(true);
    try {
      const fxTex = this.entities?.fx.atlas;
      if (fxTex) r.initTexture(fxTex);
      this.scene.updateMatrixWorld(true);
      this.shadows.dynamicScene.updateMatrixWorld(true);
      await Promise.all([
        compileAllAsync(r, this.scene, cam0),
        compileAllAsync(r, this.shadows.dynamicScene, this.shadows.camera),
        compileAllAsync(r, this.shadows.staticScene, this.shadows.camera),
      ]);
      for (const v of picks.length ? picks : [null]) {
        const cam = v ? this.applyDebugView(v) : this.camera;
        cam.updateMatrixWorld();
        this.shadows.render(r, 1);
        r.setRenderTarget(this.post.target);
        r.clear(true, true, false);
        r.render(this.scene, cam);
        this.post.render(r);
      }
      r.setRenderTarget(null);
      const gl = r.getContext();
      gl.readPixels(0, 0, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array(4)); // wait for the GPU
    } finally {
      show(false);
      r.info.reset();
    }
  }

  setViewmodel(vm: Viewmodel | null): void {
    this.viewmodel = vm;
  }

  resize(width: number, height: number, pixelRatio: number): void {
    this.width = width; this.height = height;
    this.reqPixelRatio = pixelRatio;
    pixelRatio = effectivePixelRatio(width, height, pixelRatio, this.maxPixels);
    this.pixelRatio = pixelRatio;
    this.renderer.setPixelRatio(pixelRatio);
    this.renderer.setSize(width, height, false);
    this.post.setSize(Math.floor(width * pixelRatio), Math.floor(height * pixelRatio));
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.debugCam.aspect = width / height;
    this.debugCam.updateProjectionMatrix();
  }

  render(world: ClientWorld, refdef: RefDef, dtMs: number): void {
    const t0 = performance.now();
    const r = this.renderer;
    this.lastWorld = world;
    if (this.gpu !== 'ok') { this.info.drawCalls = 0; this.info.triangles = 0; return; } // context lost / rebuilding
    r.info.reset();
    this.timeMs += dtMs;
    this.U.uTime.value = this.timeMs / 1000;
    // camera from the refdef (CoD -> three)
    codToThree(this.tmp, refdef.origin);
    this.camera.position.set(this.tmp[0], this.tmp[1], this.tmp[2]);
    codAnglesToThreeEuler(this.euler, refdef.angles);
    this.camera.rotation.set(this.euler[0], this.euler[1], this.euler[2], 'YXZ');
    if (this.camera.fov !== refdef.fovY || this.camera.near !== Math.max(1, refdef.zNear || R_ZNEAR)) {
      this.camera.fov = refdef.fovY;
      this.camera.near = Math.max(1, refdef.zNear || R_ZNEAR);
      this.camera.updateProjectionMatrix();
    }
    const cam = this.debugView ? this.applyDebugView(this.debugView) : this.camera;
    cam.updateMatrixWorld();

    let dynCasters = 0;
    if (this.entities) dynCasters = this.entities.update(world, cam, dtMs);

    const before0 = r.info.render.calls;
    this.extraInfo.shadowCalls = this.shadows.render(r, dynCasters);
    void before0;
    // main pass into the post target
    r.setRenderTarget(this.post.target);
    r.clear(true, true, false);
    const b1 = r.info.render.calls;
    r.render(this.scene, cam);
    this.extraInfo.mainCalls = r.info.render.calls - b1;
    this.post.material.uniforms.uFlash.value = this.entities?.screenFlash ?? 0;
    this.post.render(r);
    this.info.drawCalls = r.info.render.calls;
    this.info.triangles = r.info.render.triangles;
    this.info.textures = r.info.memory.textures;
    if (this.viewmodel && !this.debugView && this.viewmodelReady(this.viewmodel)) {
      const b2 = r.info.render.calls;
      r.clearDepth();
      r.render(this.viewmodel.scene, this.viewmodel.camera);
      this.extraInfo.vmCalls = r.info.render.calls - b2;
      this.info.drawCalls = r.info.render.calls;
      this.info.triangles = r.info.render.triangles;
    }
    this.extraInfo.frameMs = performance.now() - t0;
  }

  /**
   * The viewmodel pass compiled its programs on its first draw (verify perf-spikes: 4 links, ~0.55 s frame
   * right after MATCH/SPAWN: mtl_t6_wpn_knife_base, mtl_viewarm_watchglass, ...). Compile the whole viewmodel
   * scene first, hidden parts included, in parallel where KHR_parallel_shader_compile exists, and draw it
   * once that is done (at most VM_WARM_MAX_MS later; the spawn raise anim covers it).
   */
  private viewmodelReady(vm: Viewmodel): boolean {
    if (this.vmWarm === 'ready') return true;
    const now = performance.now();
    if (this.vmWarm === 'none') {
      let meshes = 0;
      vm.scene.traverse((o) => { if ((o as THREE.Mesh).isMesh) meshes++; });
      if (meshes === 0) return true; // not loaded yet: nothing to compile
      this.vmWarm = 'compiling';
      this.vmWarmStart = now;
      void compileAllAsync(this.renderer, vm.scene, vm.camera).catch(() => {}).then(() => {
        this.vmTexQueue = collectTextures(vm.scene);
        this.vmWarm = 'uploading';
      });
      return false;
    }
    if (this.vmWarm === 'uploading') {
      // one texture per frame (each big map + its mips is a GPU stall of its own), then draw
      const t = this.vmTexQueue.pop();
      if (t) this.renderer.initTexture(t);
      if (!this.vmTexQueue.length) { this.vmWarm = 'ready'; return true; }
    }
    if (now - this.vmWarmStart > VM_WARM_MAX_MS) { this.vmWarm = 'ready'; return true; }
    return false;
  }

  private applyDebugView(v: DebugView): THREE.Camera {
    if (v.ortho) {
      const s = v.ortho, a = this.width / this.height;
      const c = this.orthoCam;
      c.left = -s * a; c.right = s * a; c.top = s; c.bottom = -s;
      c.near = 1; c.far = 40000;
      c.position.set(v.pos[0], v.pos[1], v.pos[2]);
      c.up.set(0, 0, -1);
      c.lookAt(v.pos[0], 0, v.pos[2]);
      c.updateProjectionMatrix();
      return c;
    }
    const c = this.debugCam;
    c.fov = v.fov ?? 65;
    c.near = 4;
    c.updateProjectionMatrix();
    c.up.set(0, 1, 0);
    c.position.set(v.pos[0], v.pos[1], v.pos[2]);
    if (v.target) c.lookAt(v.target[0], v.target[1], v.target[2]);
    return c;
  }

  /** The same viewpoints as tools/map/preview/render.mjs (three space). */
  private debugViews(): DebugView[] {
    const meta = this.meta, sp = this.spawnsJson;
    if (!meta || !sp) return [];
    const views: DebugView[] = [];
    const bmin = meta.bounds.playerCollision.min, bmax = meta.bounds.playerCollision.max;
    const half = (Math.max(bmax[0] - bmin[0], bmax[2] - bmin[2]) / 2) * 1.05;
    views.push({ name: 'top', ortho: half, pos: [(bmin[0] + bmax[0]) / 2, 8000, (bmin[2] + bmax[2]) / 2] });
    const im = sp.intermission?.[0];
    if (im) {
      const [p, y] = im.angles; const yr = (y * Math.PI) / 180, pr = (p * Math.PI) / 180;
      const d = [Math.cos(pr) * Math.cos(yr), -Math.sin(pr), -Math.cos(pr) * Math.sin(yr)];
      views.push({ name: 'intermission', pos: im.origin, target: im.origin.map((v, i) => v + d[i] * 1000), fov: 70 });
    }
    const dm = sp.spawns?.mp_dm_spawn ?? [];
    for (const k of [0, 7, 14, 21, 28]) {
      const s = dm[k]; if (!s) continue;
      const yr = (s.angles[1] * Math.PI) / 180;
      const eye = [s.origin[0], s.origin[1] + 60, s.origin[2]];
      views.push({ name: `spawn${k}`, pos: eye, target: [eye[0] + Math.cos(yr) * 1000, eye[1], eye[2] - Math.sin(yr) * 1000], fov: 65 });
    }
    const c = sp.mapCenter ?? [0, 0, 0];
    views.push({ name: 'overview', pos: [c[0] + 2600, 1400, c[2] + 2600], target: [c[0], 0, c[2]], fov: 60 });
    return views;
  }

  private installDebugHandle(): void {
    const self = this;
    window.__render = {
      ready: false,
      setDebugCamera(v) { self.debugView = v; },
      views() { return self.debugViews(); },
      setExposure(k) { self.exposureK = k; self.U.uExposure.value = k; },
      getExposure() { return self.exposureK; },
      setOption(name, value) {
        switch (name) {
          case 'shadows': self.shadows.enabled = !!value; break;
          case 'dynShadows': self.shadows.dynamicEnabled = !!value; break;
          case 'lut': self.post.material.uniforms.uLutMix.value = Number(value); break;
          case 'cullScale': self.U.uCullScale.value = Number(value); break;
          case 'primaryLights': self.U.uPLScale.value = Number(value); break;
          case 'fog': self.U.uFogB.value.x = self.U.uFogB.value.y = value ? (self.meta?.fog?.fogOpacity ?? 0.9) : 0; break;
          case 'maxPixels': self.maxPixels = Number(value); self.resize(self.width, self.height, self.reqPixelRatio); break;
          case 'hideGroups': // debug: hide world groups whose key contains the given substring ('' = show all)
            for (const m of self.world?.root.children ?? []) m.visible = !(String(value) && m.name.includes(String(value)));
            break;
          default: self.entities?.setOption(name, value);
        }
      },
      inspect() {
        const out: Record<string, unknown>[] = [];
        self.scene.traverse((o) => {
          if (!o.name.startsWith('player_') && !o.name.startsWith('missile_')) return;
          const wp = o.getWorldPosition(new THREE.Vector3());
          let meshes = 0, skinned = 0;
          const bones: Record<string, number[]> = {};
          o.traverse((c) => {
            if ((c as THREE.Mesh).isMesh) meshes++;
            if ((c as THREE.SkinnedMesh).isSkinnedMesh) skinned++;
            if (['cod_root', 'tag_origin', 'j_mainroot', 'pelvis', 'j_head', 'j_ankle_le', 'tag_weapon_right'].includes(c.name)) bones[c.name] = c.getWorldPosition(new THREE.Vector3()).sub(wp).toArray().map(Math.round);
          });
          out.push({ name: o.name, pos: wp.toArray().map(Math.round), visible: o.visible, meshes, skinned, bones });
        });
        return out;
      },
      fx(kind, pos, surface) { self.entities?.debugFx(kind, pos, surface ?? 0); },
      focusMissile(index = 0, dist = 60) {
        const ms = self.lastWorld?.missiles[index];
        if (!ms) return null;
        const o = ms.origin;
        self.debugView = { name: 'focusm', pos: [o[0] + dist * 0.7, o[2] + dist * 0.5, -(o[1] + dist * 0.5)], target: [o[0], o[2], -o[1]], fov: 50 };
        return { kind: ms.kind, state: ms.state, explodeAt: ms.explodeAt, renderTime: self.lastWorld!.renderTime, origin: [...o] };
      },
      focusPlayer(cn, dist = 120, yawOffset = 0, height = 50) {
        const p = self.lastWorld?.getPlayer(cn);
        if (!p) return false;
        const yaw = ((p.angles[1] + yawOffset) * Math.PI) / 180;
        const o = p.origin;
        const eye = [o[0] + Math.cos(yaw) * dist, o[1] + Math.sin(yaw) * dist, o[2] + height];
        self.debugView = { name: 'focus', pos: [eye[0], eye[2], -eye[1]], target: [o[0], o[2] + 36, -o[1]], fov: 60 };
        return true;
      },
      stats() {
        return {
          info: { ...self.info }, passes: { ...self.extraInfo }, world: self.world?.stats ?? null,
          shadowSize: self.shadows.size, exposure: self.exposureK, gpu: self.gpu, gpuRestores: self.gpuRestores, mapLoads: { ...self.loadCount },
          pixelRatio: self.pixelRatio, requestedPixelRatio: self.reqPixelRatio, maxPixels: self.maxPixels, entities: self.entities?.stats() ?? null,
          programs: self.renderer.info.programs?.length ?? 0,
          programNames: (self.renderer.info.programs ?? []).map((p) => p.name), geometries: self.renderer.info.memory.geometries,
          skeletons: (() => { const k = new Set<THREE.Skeleton>(); self.scene.traverse((o) => { const sm = o as THREE.SkinnedMesh; if (sm.isSkinnedMesh) k.add(sm.skeleton); }); return k.size; })(),
        };
      },
    };
  }

  dispose(): void {
    this.entities?.dispose();
    this.shadows.dispose();
    this.post.dispose();
    this.renderer.dispose();
  }
}
